import { useEffect, useState } from 'react';
import { Plus, Trash2, Search, Ban } from 'lucide-react';
import { useAuth } from '@/contexts/AuthContext';
import {
  getDoNotContactList,
  addToDoNotContact,
  removeFromDoNotContact,
  type DoNotContactEntry,
} from '@traction/api-client';
import { formatRelativeTime } from '@traction/shared';

export function DoNotContactPage() {
  const { user } = useAuth();
  const [entries, setEntries] = useState<DoNotContactEntry[]>([]);
  const [loading, setLoading] = useState(true);
  const [searchQuery, setSearchQuery] = useState('');
  const [showAddModal, setShowAddModal] = useState(false);
  const [newEmail, setNewEmail] = useState('');
  const [newReason, setNewReason] = useState('');
  const [adding, setAdding] = useState(false);

  useEffect(() => {
    loadEntries();
  }, [user]);

  async function loadEntries() {
    if (!user) return;
    try {
      const data = await getDoNotContactList(user.uid);
      setEntries(data);
    } catch (error) {
      console.error('Failed to load DNC list:', error);
    } finally {
      setLoading(false);
    }
  }

  async function handleAdd() {
    if (!user || !newEmail.trim()) return;
    setAdding(true);

    try {
      await addToDoNotContact(user.uid, newEmail.trim(), newReason.trim() || undefined);
      await loadEntries();
      setNewEmail('');
      setNewReason('');
      setShowAddModal(false);
    } catch (error) {
      console.error('Failed to add to DNC list:', error);
    } finally {
      setAdding(false);
    }
  }

  async function handleRemove(entryId: string) {
    if (!user) return;

    try {
      await removeFromDoNotContact(user.uid, entryId);
      setEntries((prev) => prev.filter((e) => e.id !== entryId));
    } catch (error) {
      console.error('Failed to remove from DNC list:', error);
    }
  }

  async function handleBulkAdd(text: string) {
    if (!user) return;

    const emails = text
      .split(/[\n,;]/)
      .map((e) => e.trim().toLowerCase())
      .filter((e) => e.includes('@'));

    setAdding(true);
    try {
      for (const email of emails) {
        await addToDoNotContact(user.uid, email);
      }
      await loadEntries();
      setNewEmail('');
      setShowAddModal(false);
    } catch (error) {
      console.error('Failed to bulk add:', error);
    } finally {
      setAdding(false);
    }
  }

  const filteredEntries = searchQuery
    ? entries.filter(
        (e) =>
          e.email.toLowerCase().includes(searchQuery.toLowerCase()) ||
          e.reason?.toLowerCase().includes(searchQuery.toLowerCase())
      )
    : entries;

  return (
    <div className="mx-auto max-w-4xl px-4 py-8">
      <div className="flex items-center justify-between">
        <div>
          <h1 className="font-heading text-lg font-medium text-[#F5F5F5]">Do Not Contact List</h1>
          <p className="mt-1 text-sm text-cacao-muted">
            Emails on this list will be excluded from lead generation
          </p>
        </div>
        <button
          onClick={() => setShowAddModal(true)}
          className="flex items-center gap-2 rounded-md bg-cacao-gold px-3 py-2 text-xs font-medium text-cacao-dark hover:bg-cacao-gold-hover transition-colors"
        >
          <Plus className="h-3.5 w-3.5" />
          Add Email
        </button>
      </div>

      <div className="mt-6">
        <div className="relative">
          <Search className="absolute left-3 top-1/2 h-4 w-4 -translate-y-1/2 text-cacao-muted" />
          <input
            type="text"
            value={searchQuery}
            onChange={(e) => setSearchQuery(e.target.value)}
            placeholder="Search emails..."
            className="w-full rounded-md border border-cacao-dark-tertiary bg-cacao-dark-tertiary py-2 pl-9 pr-3 text-sm text-[#F5F5F5] placeholder-cacao-text-tertiary focus:border-cacao-gold focus:outline-none focus:ring-1 focus:ring-cacao-gold"
          />
        </div>
      </div>

      <div className="mt-4 rounded-lg border border-cacao-dark-tertiary bg-cacao-dark-secondary">
        {loading ? (
          <div className="flex items-center justify-center py-12">
            <div className="h-6 w-6 animate-spin rounded-full border-2 border-cacao-dark-tertiary border-t-cacao-gold" />
          </div>
        ) : filteredEntries.length === 0 ? (
          <div className="flex flex-col items-center justify-center py-12 text-center">
            <div className="flex h-12 w-12 items-center justify-center rounded-full bg-cacao-dark-tertiary">
              <Ban className="h-6 w-6 text-cacao-muted" />
            </div>
            <p className="mt-4 text-sm font-medium text-[#F5F5F5]">
              {searchQuery ? 'No matches found' : 'No blocked emails'}
            </p>
            <p className="mt-1 text-xs text-cacao-muted">
              {searchQuery
                ? 'Try a different search term'
                : 'Add emails you want to exclude from outreach'}
            </p>
          </div>
        ) : (
          <div className="divide-y divide-cacao-dark-tertiary">
            <div className="flex items-center gap-4 px-4 py-2 text-xs font-medium text-cacao-muted">
              <div className="flex-1">Email</div>
              <div className="w-40">Reason</div>
              <div className="w-24">Added</div>
              <div className="w-10" />
            </div>
            {filteredEntries.map((entry) => (
              <div
                key={entry.id}
                className="flex items-center gap-4 px-4 py-3 hover:bg-cacao-dark-tertiary/30 transition-colors"
              >
                <div className="flex-1 truncate text-sm text-[#F5F5F5]">{entry.email}</div>
                <div className="w-40 truncate text-xs text-cacao-muted">
                  {entry.reason || '-'}
                </div>
                <div className="w-24 text-xs text-cacao-text-tertiary">
                  {formatRelativeTime(entry.created_at)}
                </div>
                <div className="w-10">
                  <button
                    onClick={() => handleRemove(entry.id)}
                    className="rounded p-1 text-cacao-muted hover:bg-red-500/20 hover:text-red-400 transition-colors"
                    title="Remove from list"
                  >
                    <Trash2 className="h-4 w-4" />
                  </button>
                </div>
              </div>
            ))}
          </div>
        )}
      </div>

      <div className="mt-4 text-xs text-cacao-text-tertiary">
        {entries.length} email{entries.length !== 1 ? 's' : ''} blocked
      </div>

      {/* Add Email Modal */}
      {showAddModal && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/60">
          <div className="w-full max-w-md rounded-lg border border-cacao-dark-tertiary bg-cacao-dark-secondary p-6">
            <h2 className="font-heading text-lg font-medium text-[#F5F5F5]">
              Add to Do Not Contact
            </h2>
            <p className="mt-1 text-xs text-cacao-muted">
              Enter one or more emails (separated by commas or new lines)
            </p>

            <div className="mt-4 space-y-4">
              <div>
                <label className="block text-xs font-medium text-cacao-muted">
                  Email address(es)
                </label>
                <textarea
                  value={newEmail}
                  onChange={(e) => setNewEmail(e.target.value)}
                  placeholder="john@example.com&#10;jane@example.com"
                  rows={3}
                  className="mt-1 block w-full rounded-md border border-cacao-dark-tertiary bg-cacao-dark-tertiary px-3 py-2 text-sm text-[#F5F5F5] placeholder-cacao-text-tertiary focus:border-cacao-gold focus:outline-none focus:ring-1 focus:ring-cacao-gold"
                />
              </div>

              <div>
                <label className="block text-xs font-medium text-cacao-muted">
                  Reason (optional)
                </label>
                <input
                  type="text"
                  value={newReason}
                  onChange={(e) => setNewReason(e.target.value)}
                  placeholder="e.g., Competitor, Already a customer"
                  className="mt-1 block w-full rounded-md border border-cacao-dark-tertiary bg-cacao-dark-tertiary px-3 py-2 text-sm text-[#F5F5F5] placeholder-cacao-text-tertiary focus:border-cacao-gold focus:outline-none focus:ring-1 focus:ring-cacao-gold"
                />
              </div>
            </div>

            <div className="mt-6 flex gap-3">
              <button
                onClick={() => {
                  setShowAddModal(false);
                  setNewEmail('');
                  setNewReason('');
                }}
                className="flex-1 rounded-md border border-cacao-dark-tertiary px-3 py-2 text-sm font-medium text-cacao-muted hover:border-cacao-gold hover:text-white transition-colors"
              >
                Cancel
              </button>
              <button
                onClick={() => {
                  if (newEmail.includes('\n') || newEmail.includes(',')) {
                    handleBulkAdd(newEmail);
                  } else {
                    handleAdd();
                  }
                }}
                disabled={adding || !newEmail.trim()}
                className="flex-1 rounded-md bg-cacao-gold px-3 py-2 text-sm font-medium text-cacao-dark hover:bg-cacao-gold-hover disabled:opacity-50 transition-colors"
              >
                {adding ? 'Adding...' : 'Add to List'}
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
